import React, { useState, useEffect } from 'react'
import { string, shape, func, object } from 'prop-types'
import { usePlaidLink } from '../usePlaidLink'
import { BANK_PLAID } from '../../constants'
import CreateBankMemberMutation from './CreateBankMemberMutation.gql'
import SyncBankMemberMutation from './SyncBankMemberMutation.gql'
import GetBankMemberTokenQuery from './GetBankMemberTokenQuery.gql'

/**********************************************************************
Plaid widget

To test a broken connection, see the 1password entry
for 'Viking Plaid Connections' and follow the instructions there.

To clear out bank accounts, run `mix clean.banks` in viking
***********************************************************************/

const PlaidWidget = ({ client, email, env, memberToFix, name, onConnectedSuccess, onFailedToConnect }) => {
  // with plaid, once they click "reconnect" on the bank that has an error, we have to ask viking for a token
  // to pass to the widget
  const [fixMemberToken, setFixMemberToken] = useState(null)

  // This is once the widget has validated their login and they have selected accounts
  const onSuccess = token => {
    client.mutate({
      mutation: CreateBankMemberMutation,
      variables: { input: { externalId: token, provider: BANK_PLAID } }
    }).then(() => {
      // give the widget a little time to finish
      setTimeout(() => {
        onConnectedSuccess()
      }, 200)
    })
  }

  // after they finish re-authorizating using the widget we need to tell the backend to sync the bank again
  const onFixedConnection = () => {
    client.mutate({
      mutation: SyncBankMemberMutation,
      variables: { input: { id: memberToFix.id } }
    }).then(() => {
      onConnectedSuccess()
    })
  }

  // plaid widget configuration
  // https://github.com/plaid/react-plaid-link#all-available-link-configuration-options
  let config = {
    clientName: 'Divvy',
    env: env.plaidEnv,
    onExit: error => {
      // this dumb error happens when the connection is fine and they just click "Continue"
      // eslint-disable-next-line camelcase
      if (error?.error_code === 'item-no-error') {
        onFixedConnection()
      } else {
        // eslint-disable-next-line camelcase
        onFailedToConnect(error?.display_message, BANK_PLAID)
      }
    },
    onSuccess: token => onSuccess(token),
    product: ['transactions'],
    publicKey: '4f49b5a20a4649577a75282a3d7718',
    userEmailAddress: email,
    userLegalName: name,
    // eslint-disable-next-line camelcase
    webhook: env.plaidWebhook
  }

  // the plaid widget is REALLY terrible at being configured and rendered more than once, so if we need to laod
  // the fix token, we don't want to render it at all until we have the fix token. So, the usePlaidLink hook only
  // initializes if there is a clientName option. What we do here is we only provide that option, and thus trigger
  // initilization and first render, if 1) there is no member to fix 2) the fix member token is ready and loaded
  if (memberToFix) {
    config = {
      ...config,
      clientName: (!memberToFix || fixMemberToken) && config.clientName,
      onSuccess: onFixedConnection,
      token: fixMemberToken
    }
  }

  // https://github.com/plaid/react-plaid-link
  const { error, exit, open, ready } = usePlaidLink(config)

  useEffect(() => {
    if (!ready) return

    // if we need to fix a member and we haven't already loaded the token
    if (memberToFix && !fixMemberToken) {
      client.query({
        fetchPolicy: 'no-cache',
        query: GetBankMemberTokenQuery,
        variables: { memberId: memberToFix.id }
      }).then(payload => {
        // this will cause a re-render and cause the plaid widget be configured with this token
        setFixMemberToken(
          payload?.data?.node?.plaidPublicToken
        )
      })
    } else {
      open()
    }

    return () => {
      exit && exit()

      // TEMPORARY I HOPE:
      // these plaid widget dummies put a style="overflow: hidden" on the body when it opens and then they don't remove
      // it when theres an error so the user can't scroll anymore. so we clean up their mess
      setTimeout(() => {
        document.body.removeAttribute('style')
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberToFix, fixMemberToken, ready, open])

  // plaid handles its own dom manipuation, so we don't really care to render anything unless theres an error
  return <span>{error}</span>
}

PlaidWidget.propTypes = {
  client: shape({
    query: func
  }).isRequired,
  email: string,
  env: object.isRequired,
  memberToFix: shape({
    id: string
  }),
  name: string,
  onConnectedSuccess: func.isRequired,
  onFailedToConnect: func.isRequired
}

export default PlaidWidget
